import { Alert, AlertIcon, Button, SimpleGrid, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { useState } from 'react';
import { MdFileDownload } from 'react-icons/md';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate, TranslationKeys } from '../../../i18n';
import { EventType } from '../../../models/event/dto/event.dto';
import {
    MAX_REPORT_RANGE_DAYS,
    ReportRangeError,
    useEventAttendanceReport,
    validateReportRange,
} from '../hooks/useEventAttendanceReport';
import {
    buildAttendanceExportFilename,
    buildEventAttendanceCsv,
    downloadCsv,
} from '../utils/exportEventAttendanceCsv';

/** `YYYY-MM-DD` in the operator's own timezone, which is what `<input type="date">` speaks. */
function toDateInputValue(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}

/** Default window: the last four weeks, which is the span a club replans on. */
function defaultRange() {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 27);
    return { from: toDateInputValue(start), to: toDateInputValue(today) };
}

const ALL_TYPES = 'all';

const RANGE_ERROR_MESSAGE: Record<ReportRangeError, string> = {
    dates_required: translate('event.attendance_export.error_dates_required'),
    range_inverted: translate('event.attendance_export.error_range_inverted'),
    range_too_long: translate('event.attendance_export.error_range_too_long', {
        days: MAX_REPORT_RANGE_DAYS,
    }),
};

/**
 * Group-class attendance export: pick a window, get one CSV line per session.
 *
 * The type selector defaults to "cours collectifs" because that is the
 * question this export answers, but the venue's other sessions are one option
 * away — an operator who wants the whole planning picture should not have to
 * run two exports and stitch them.
 */
export default function ExportAttendanceModal({ locationId }: { locationId?: string }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    // Lazy initializer — `defaultRange()` reads the clock, and re-running it on
    // every render would be pure waste.
    const [initialRange] = useState(defaultRange);
    const [from, setFrom] = useState(initialRange.from);
    const [to, setTo] = useState(initialRange.to);
    const [eventType, setEventType] = useState<string>(EventType.CLASS);
    const [includeCancelled, setIncludeCancelled] = useState(true);
    const toast = useToast();
    const { mutateAsync: fetchReport, isLoading } = useEventAttendanceReport();

    // Validated against the window the request will actually carry, so the
    // dialog and the API can never disagree about what fits — see
    // `validateReportRange`.
    const rangeErrorCode = validateReportRange(from, to);
    const rangeError = rangeErrorCode ? RANGE_ERROR_MESSAGE[rangeErrorCode] : undefined;

    const handleExport = async () => {
        if (!locationId || rangeError) return;
        try {
            const report = await fetchReport({
                locationId,
                from,
                to,
                eventType: eventType === ALL_TYPES ? undefined : (eventType as EventType),
                includeCancelled,
            });

            if (!report?.rows?.length) {
                toast({
                    title: translate('event.attendance_export.empty'),
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            const csv = buildEventAttendanceCsv(report, (key) => String(translate(key as TranslationKeys) ?? key));
            downloadCsv(csv, buildAttendanceExportFilename(from, to));

            // A truncated export that announces itself as a success is how a
            // club ends up replanning on a partial month.
            if (report.truncated) {
                toast({
                    title: translate('event.attendance_export.truncated', { count: report.rows.length }),
                    status: 'warning',
                    duration: 8000,
                    isClosable: true,
                });
            } else {
                toast({
                    title: translate('event.attendance_export.success'),
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });
            }
            onClose();
        } catch (error: any) {
            toast({
                title: translate('event.attendance_export.error'),
                description: error?.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    return (
        <>
            <Button
                variant="outline"
                colorScheme="brand"
                leftIcon={<MdFileDownload />}
                onClick={onOpen}
                isDisabled={!locationId}
                data-testid="open-attendance-export"
            >
                {translate('event.attendance_export.button')}
            </Button>
            <FormModal
                isOpen={isOpen}
                onClose={onClose}
                title={translate('event.attendance_export.title')}
                onSubmit={handleExport}
                submitLabel={translate('event.attendance_export.submit')}
                cancelLabel={translate('cancel')}
                isSubmitting={isLoading}
                isSubmitDisabled={!locationId || Boolean(rangeError)}
                size="lg"
            >
                <FormStack>
                    <Text fontSize="sm" color="gray.500">
                        {translate('event.attendance_export.description')}
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap="16px">
                        <FormField
                            type="date"
                            label={translate('event.attendance_export.from')}
                            value={from}
                            onChange={setFrom}
                            isRequired
                            error={rangeError}
                        />
                        <FormField
                            type="date"
                            label={translate('event.attendance_export.to')}
                            value={to}
                            onChange={setTo}
                            isRequired
                        />
                    </SimpleGrid>
                    <FormField
                        type="select"
                        label={translate('event.attendance_export.type_filter')}
                        value={eventType}
                        onChange={setEventType}
                        options={[
                            { value: EventType.CLASS, label: translate('event.attendance_export.type_class') },
                            { value: ALL_TYPES, label: translate('event.attendance_export.type_all') },
                        ]}
                    />
                    <FormField
                        type="switch"
                        label={translate('event.attendance_export.include_cancelled')}
                        helperText={translate('event.attendance_export.include_cancelled_helper')}
                        isChecked={includeCancelled}
                        onChange={setIncludeCancelled}
                    />
                    <Alert status="info" borderRadius="12px" fontSize="sm">
                        <AlertIcon />
                        {translate('event.attendance_export.attendance_notice')}
                    </Alert>
                </FormStack>
            </FormModal>
        </>
    );
}
